import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';

import { ORDER_STATUS_COLORS, ORDER_STATUS_STRINGS } from '@domain/orderStatus';
import {
  ActionIcon,
  Anchor,
  Badge,
  Center,
  Group,
  Loader,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconInfoSquareRoundedFilled } from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';
import { priceToDecimal } from '@utils/price';

import {
  GetOrdersRequestParams,
  GetOrdersSortBy,
  useGetOrdersCountQuery,
  useGetOrdersQuery,
} from '@api/orders.api';
import { Order } from '@interfaces/orders.interface';

import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';
import useUserRoleGuard from '@hooks/useUserRoleGuard';
import {
  PAYMENT_STATUS_COLORS,
  PAYMENT_STATUS_STRINGS,
} from '@domain/paymentStatus';

export default function Orders() {
  const [searchParams] = useSearchParams();

  const userIsAdmin = useUserRoleGuard('admin');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetOrdersRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'bluedayOrderId',
    sortOrder: 'desc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Order>>({
    columnAccessor: 'bluedayOrderId',
    direction: 'desc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GetOrdersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================

  // Get initial orders count
  const { data: ordersCount = { count: 0 }, isLoading: isLoadingOrdersCount } =
    useGetOrdersCountQuery(searchQuery);

  const { data = [], isLoading } = useGetOrdersQuery({
    ...filters,
    searchQuery,
  });

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Lista ordini',
    });
  }, [setLayoutProps]);

  return isLoading || isLoadingOrdersCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : (
    <>
      <Group mb="lg">
        <SearchBar
          placeholder={
            userIsAdmin
              ? 'Cerca numero ordine/cliente'
              : 'Cerca numero ordine/rif. cliente'
          }
          value={filters.searchQuery}
          onChange={(newValue) =>
            setFilters({ ...filters, searchQuery: newValue })
          }
        />
      </Group>
      <DataTable
        striped
        withTableBorder
        noRecordsText="Nessun ordine trovato per i filtri selezionati"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'date',
            title: 'Data',
            sortable: true,
            render: (record: Order) => dateToDateString(new Date(record.date)),
          },
          {
            accessor: 'bluedayOrderId',
            title: 'Numero ordine',
            sortable: true,
            render: (record: Order) =>
              record.bluedayOrderId || record.id || '-',
          },
          ...(userIsAdmin
            ? [
                {
                  accessor: 'client.user.name',
                  title: 'Cliente',
                  render: (record: Order) => (
                    <Anchor
                      to={`/utenti/${record.client?.user.id}`}
                      component={Link}
                      size="sm"
                    >
                      {record.client?.user.name || '-'}
                    </Anchor>
                  ),
                },
              ]
            : [
                {
                  accessor: 'customerRef',
                  title: 'Rif. cliente',
                  render: (record: Order) => record.customerRef || '-',
                },
                {
                  accessor: 'shippingDate',
                  title: 'Data sped. prevista',
                  render: (record: Order) =>
                    record.shippingDate
                      ? dateToDateString(new Date(record.shippingDate))
                      : '-',
                },
              ]),
          {
            accessor: 'total',
            title: 'Totale (Iva In.)',
            render: (record: Order) => `€ ${priceToDecimal(record.total)}`,
          },
          {
            accessor: 'status',
            title: 'Stato',
            render: (record: Order) => (
              <Badge
                color={
                  ORDER_STATUS_COLORS[
                    record.status as keyof typeof ORDER_STATUS_COLORS
                  ]
                }
              >
                {
                  ORDER_STATUS_STRINGS[
                    record.status as keyof typeof ORDER_STATUS_STRINGS
                  ]
                }
              </Badge>
            ),
          },
          ...(userIsAdmin
            ? [
                {
                  accessor: 'lastPaymentStatus',
                  title: 'Stato pagamento',
                  render: (record: Order) => (
                    <Badge
                      color={
                        PAYMENT_STATUS_COLORS[
                          record.lastPaymentStatus || 'pending'
                        ]
                      }
                    >
                      {
                        PAYMENT_STATUS_STRINGS[
                          record.lastPaymentStatus || 'pending'
                        ]
                      }
                    </Badge>
                  ),
                },
              ]
            : []),
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record: Order) => (
              <Tooltip label="Dettagli ordine">
                <ActionIcon
                  variant="subtle"
                  component={Link}
                  to={`/ordini/${record.id}`}
                >
                  <IconInfoSquareRoundedFilled />
                </ActionIcon>
              </Tooltip>
            ),
          },
        ]}
        records={data}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={ordersCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
